
import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const modelFormSchema = z.object({
  name: z.string().min(2, "Model name must be at least 2 characters"),
});

type BikeModel = {
  id: string;
  name: string;
  created_at: string;
};

const BikeModelsTable = () => {
  const queryClient = useQueryClient();
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false);
  const [editingModel, setEditingModel] = React.useState<BikeModel | null>(null);
  const [deleteModelId, setDeleteModelId] = React.useState<string | null>(null);

  const form = useForm<z.infer<typeof modelFormSchema>>({
    resolver: zodResolver(modelFormSchema),
    defaultValues: {
      name: "",
    },
  });

  const { data: models = [], isLoading } = useQuery({
    queryKey: ["bikeModels"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bike_models")
        .select("*")
        .order("name");

      if (error) throw error;
      return data as BikeModel[];
    },
  });

  const addModelMutation = useMutation({
    mutationFn: async (name: string) => {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from("bike_models")
        .insert([{ name, created_at: now }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bikeModels"] });
      toast.success("Bike model added successfully");
      setIsAddDialogOpen(false);
      form.reset();
    },
    onError: (error: Error) => {
      toast.error(`Failed to add bike model: ${error.message}`);
    },
  });

  const updateModelMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { error } = await supabase
        .from("bike_models")
        .update({ name })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bikeModels"] });
      toast.success("Bike model updated successfully");
      setEditingModel(null);
      form.reset();
    },
    onError: (error: Error) => {
      toast.error(`Failed to update bike model: ${error.message}`);
    },
  });

  const deleteModelMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data: bikes, error: checkError } = await supabase
        .from("bikes")
        .select("id")
        .eq("model_id", id)
        .limit(1);

      if (checkError) throw checkError;
      if (bikes && bikes.length > 0) {
        throw new Error("Cannot delete model: There are bikes using this model");
      }

      const { error } = await supabase
        .from("bike_models")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bikeModels"] });
      toast.success("Bike model deleted successfully");
      setDeleteModelId(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete bike model: ${error.message}`);
      setDeleteModelId(null);
    },
  });

  const onSubmit = (values: z.infer<typeof modelFormSchema>) => {
    if (editingModel) {
      updateModelMutation.mutate({ id: editingModel.id, name: values.name });
    } else {
      addModelMutation.mutate(values.name);
    }
  };
  
  // Helper function to format date properly
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    
    const date = new Date(dateString);
    // Check if the date is valid before formatting
    if (isNaN(date.getTime())) {
      return "N/A";
    }
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Bike Models</h2>
        <Dialog open={isAddDialogOpen || !!editingModel} onOpenChange={(open) => {
          if (!open) {
            setIsAddDialogOpen(false);
            setEditingModel(null);
            form.reset();
          }
        }}>
          <DialogTrigger asChild>
            <Button onClick={() => setIsAddDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Model
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingModel ? "Edit Bike Model" : "Add New Bike Model"}
              </DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Model Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter model name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsAddDialogOpen(false);
                      setEditingModel(null);
                      form.reset();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit">
                    {editingModel ? "Update" : "Add"} Model
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Model Name</TableHead>
              <TableHead>Created At</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {models.map((model) => (
              <TableRow key={model.id}>
                <TableCell>{model.name}</TableCell>
                <TableCell>
                  {formatDate(model.created_at)}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingModel(model);
                        form.setValue("name", model.name);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                      <span className="sr-only">Edit</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:bg-destructive/10"
                      onClick={() => setDeleteModelId(model.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">Delete</span>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {models.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-4">
                  No bike models found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={Boolean(deleteModelId)} onOpenChange={() => setDeleteModelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Bike Model</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Are you sure you want to delete this bike model?</p>
              <p className="font-medium text-destructive">
                Warning: This action will fail if there are any bikes currently using this model.
                Please ensure all bikes are reassigned to different models before deletion.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteModelId && deleteModelMutation.mutate(deleteModelId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BikeModelsTable;
